import React, { useState, useRef, useEffect } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts"
import {
  ArrowLeft, ArrowRight, ArrowUpRight, BarChart2, TrendingUp,
  PieChart as PieIcon, ChevronDown, Video, Radio, Download,
  Users, MessageSquare, HelpCircle, FileText,
  Smile, BookOpen, Star, CheckCircle2, XCircle, Mail, Bell,
  BarChart3, X, PhoneOff, MicOff, Lock, Unlock,
  VolumeX, Volume2, UserX, UserCheck, VideoOff, Link2, ExternalLink,
  MoreHorizontal
} from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { NoData } from "@/clientWeb/components/ui/no-data"
import nodataAnalyticsImg from "@/assets/nodataanalytics.svg"
import { Badge } from "@/clientWeb/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/clientWeb/components/ui/table"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from "@/clientWeb/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/clientWeb/components/ui/select"

// ── Palette ────────────────────────────────────────────────────────────────────
const C = ["#0ea5e9", "#8b5cf6", "#f97316", "#10b981", "#ec4899", "#f59e0b", "#6366f1"]
const AVATAR_COLORS = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6"]
type ChartType = "bar" | "line" | "pie"

// ── Mock data (mirrors creation-flow fields) ──────────────────────────────────
const insightData = [
  { date: "Apr 11", webinars: 4, registered: 180, attended: 142, withReg: 110, withoutReg: 32 },
  { date: "Apr 12", webinars: 2, registered: 95,  attended: 74,  withReg: 60,  withoutReg: 14 },
  { date: "Apr 13", webinars: 6, registered: 320, attended: 261, withReg: 200, withoutReg: 61 },
  { date: "Apr 14", webinars: 3, registered: 140, attended: 108, withReg: 90,  withoutReg: 18 },
  { date: "Apr 15", webinars: 8, registered: 510, attended: 430, withReg: 320, withoutReg: 110 },
  { date: "Apr 16", webinars: 5, registered: 280, attended: 220, withReg: 170, withoutReg: 50 },
  { date: "Apr 17", webinars: 7, registered: 390, attended: 310, withReg: 240, withoutReg: 70 },
]

const registrationBreakdown = [
  { name: "With Registration", value: 1190, color: C[0] },
  { name: "Without Registration", value: 355, color: C[2] },
]

const engagementStats = [
  { label: "Chat Messages",   val: 4320, icon: MessageSquare, color: "text-blue-500",   bg: "bg-blue-50 dark:bg-blue-900/20"   },
  { label: "Q&A Questions",   val: 1940, icon: HelpCircle,    color: "text-purple-500", bg: "bg-purple-50 dark:bg-purple-900/20" },
  { label: "Poll Votes",      val: 2480, icon: BarChart3,     color: "text-orange-500", bg: "bg-orange-50 dark:bg-orange-900/20" },
  { label: "Reactions",       val: 8710, icon: Smile,         color: "text-pink-500",   bg: "bg-pink-50 dark:bg-pink-900/20"   },
  { label: "Resources DL",    val: 620,  icon: BookOpen,      color: "text-teal-500",   bg: "bg-teal-50 dark:bg-teal-900/20"   },
  { label: "Survey Submits",  val: 980,  icon: FileText,      color: "text-indigo-500", bg: "bg-indigo-50 dark:bg-indigo-900/20" },
]

const pollResults = [
  { question: "How did you hear about this webinar?", total: 310, options: [
    { text: "Social Media",  votes: 128 },
    { text: "Email Invite",  votes: 96  },
    { text: "Word of Mouth", votes: 62  },
    { text: "Search",        votes: 24  },
  ]},
  { question: "What best describes your role?", total: 290, options: [
    { text: "Developer",   votes: 140 },
    { text: "Manager",     votes: 80  },
    { text: "Designer",    votes: 45  },
    { text: "Other",       votes: 25  },
  ]},
]

const surveyData = {
  submitted: 980, total: 1545, avgRating: 4.3,
  questions: [
    { q: "Overall experience", type: "star",   avgScore: 4.3 },
    { q: "Content quality",    type: "star",   avgScore: 4.6 },
    { q: "Speaker clarity",    type: "star",   avgScore: 4.1 },
    { q: "Would recommend?",   type: "single", yes: 88, no: 12 },
  ]
}

const speakersData = [
  { name: "Sarah Wilson",  org: "TechCorp",  sessions: 4, avgAttend: 340, rating: 4.8 },
  { name: "Mike Chen",     org: "StartupXY", sessions: 3, avgAttend: 280, rating: 4.6 },
  { name: "Emma Davis",    org: "DevCo",     sessions: 2, avgAttend: 220, rating: 4.7 },
  { name: "Alex Johnson",  org: "CloudInc",  sessions: 5, avgAttend: 195, rating: 4.4 },
]

const attendeeFlowData = [
  { time: "0 min",  count: 0   },
  { time: "5 min",  count: 120 },
  { time: "10 min", count: 280 },
  { time: "20 min", count: 390 },
  { time: "30 min", count: 420 },
  { time: "45 min", count: 400 },
  { time: "60 min", count: 310 },
  { time: "75 min", count: 220 },
  { time: "90 min", count: 150 },
]

const notificationStats = [
  { label: "1st Reminder Sent",   sent: 1545, opened: 1102, rate: 71 },
  { label: "2nd Reminder Sent",   sent: 1545, opened: 890,  rate: 58 },
  { label: "Thank You Email",     sent: 980,  opened: 720,  rate: 73 },
  { label: "Follow-up Email",     sent: 980,  opened: 540,  rate: 55 },
]

const deviceData = [
  { name: "Desktop", value: 58 },
  { name: "Mobile",  value: 33 },
  { name: "Tablet",  value: 9  },
]

const topHostsData = [
  { name: "Sarah Wilson", email: "sarah@co.com", webinars: 12, attendees: 680, rating: 4.8 },
  { name: "Mike Chen",    email: "mike@co.com",  webinars: 9,  attendees: 520, rating: 4.6 },
  { name: "David Kim",    email: "david@co.com", webinars: 7,  attendees: 390, rating: 4.7 },
  { name: "Emma Davis",   email: "emma@co.com",  webinars: 6,  attendees: 310, rating: 4.5 },
]

const LIVE_WEBINARS = [
  { id: "w1", title: "AI in Modern Healthcare", host: "Sarah Wilson", attendees: 142, started: "2:00 PM",
    participants: [
      { name: "Sarah Wilson", email: "sarah@co.com", isHost: true },
      { name: "James Park",   email: "james@co.com",  isHost: false },
      { name: "Linda Scott",  email: "linda@co.com",  isHost: false },
      { name: "Tom Baker",    email: "tom@co.com",    isHost: false },
      { name: "Ana Rivera",   email: "ana@co.com",    isHost: false },
    ]
  },
  { id: "w2", title: "DevOps Best Practices", host: "Mike Chen", attendees: 98, started: "2:30 PM",
    participants: [
      { name: "Mike Chen",   email: "mike@co.com",   isHost: true  },
      { name: "Eve Jones",   email: "eve@co.com",    isHost: false },
      { name: "Robert Wu",   email: "robert@co.com", isHost: false },
      { name: "Maria Lee",   email: "maria@co.com",  isHost: false },
    ]
  },
]

const WEBINAR_RECORDINGS = [
  { id: "r1", title: "AI in Modern Healthcare",  duration: "1:12:38", size: "680 MB", views: 340, date: "Apr 15", thumb: "https://picsum.photos/400/225?random=41", host: "Sarah Wilson" },
  { id: "r2", title: "DevOps Best Practices",    duration: "0:58:14", size: "420 MB", views: 280, date: "Apr 13", thumb: "https://picsum.photos/400/225?random=42", host: "Mike Chen"    },
  { id: "r3", title: "UX Design Fundamentals",   duration: "1:05:22", size: "510 MB", views: 190, date: "Apr 11", thumb: "https://picsum.photos/400/225?random=43", host: "Emma Davis"   },
  { id: "r4", title: "Cloud Architecture 2025",  duration: "0:47:09", size: "320 MB", views: 150, date: "Apr 10", thumb: "https://picsum.photos/400/225?random=44", host: "Alex Johnson" },
]

const participantListData = [
  { id: "p1", name: "Sarah Wilson",  email: "sarah@co.com",  joinTime: "2:02 PM", leaveTime: "3:14 PM", durationMin: 72, device: "Desktop", attended: true  },
  { id: "p2", name: "James Park",    email: "james@co.com",  joinTime: "2:05 PM", leaveTime: "3:14 PM", durationMin: 69, device: "Mobile",  attended: true  },
  { id: "p3", name: "Linda Scott",   email: "linda@co.com",  joinTime: "2:10 PM", leaveTime: "2:48 PM", durationMin: 38, device: "Desktop", attended: true  },
  { id: "p4", name: "Tom Baker",     email: "tom@co.com",    joinTime: "2:03 PM", leaveTime: "3:14 PM", durationMin: 71, device: "Tablet",  attended: true  },
  { id: "p5", name: "Ana Rivera",    email: "ana@co.com",    joinTime: "2:15 PM", leaveTime: "2:55 PM", durationMin: 40, device: "Desktop", attended: true  },
  { id: "p6", name: "John Doe",      email: "john@co.com",   joinTime: "2:00 PM", leaveTime: "3:14 PM", durationMin: 74, device: "Desktop", attended: true  },
  { id: "p7", name: "Maria Kim",     email: "maria@co.com",  joinTime: "2:20 PM", leaveTime: "2:35 PM", durationMin: 15, device: "Mobile",  attended: false },
  { id: "p8", name: "Robert Ng",     email: "robert@co.com", joinTime: "2:08 PM", leaveTime: "3:14 PM", durationMin: 66, device: "Desktop", attended: true  },
]

const qaData = [
  {
    id: "q1", question: "Can you elaborate on the ML pipeline architecture?",
    askedBy: "John Doe", time: "2:25 PM", upvotes: 18, status: "answered" as const,
    answer: "The ML pipeline uses a three-stage approach: data ingestion, model training with AutoML, and real-time inference via our edge nodes.",
    answeredBy: "Sarah Wilson",
  },
  {
    id: "q2", question: "What are the cloud cost implications of scaling to 10k users?",
    askedBy: "Maria Kim", time: "2:38 PM", upvotes: 12, status: "answered" as const,
    answer: "At 10k concurrent users we project roughly $4,200/month on AWS using spot instances for training and reserved instances for inference.",
    answeredBy: "Sarah Wilson",
  },
  {
    id: "q3", question: "Is there a REST API for integrating with third-party apps?",
    askedBy: "Robert Ng", time: "2:52 PM", upvotes: 9, status: "pending" as const,
    answer: null, answeredBy: null,
  },
  {
    id: "q4", question: "How do you handle data privacy under GDPR for EU users?",
    askedBy: "Ana Rivera", time: "3:01 PM", upvotes: 7, status: "pending" as const,
    answer: null, answeredBy: null,
  },
  {
    id: "q5", question: "Can the model be deployed on-premise?",
    askedBy: "Tom Baker", time: "3:08 PM", upvotes: 5, status: "answered" as const,
    answer: "Yes, we support Kubernetes-based on-premise deployments with our enterprise tier.",
    answeredBy: "Mike Chen",
  },
]

// ── Stat card type ─────────────────────────────────────────────────────────────
type CardId = "total" | "live" | "registrants" | "attended" | "cancelled"

// ── Helpers ────────────────────────────────────────────────────────────────────
function FilterDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const t = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [])
  const filters = [t("webinar.today"), t("webinar.yesterday"), t("webinar.lastWeek"), t("webinar.lastMonth")]
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-2 border border-border bg-card rounded-lg px-3 py-1.5 text-sm font-medium text-foreground hover:border-primary transition-colors">
        {value} <ChevronDown className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-9 z-20 bg-card border border-border rounded-xl shadow-lg min-w-[140px] py-1">
          {filters.map(f => (
            <button key={f} onClick={() => { onChange(f); setOpen(false) }}
              className={`block w-full text-left px-4 py-2 text-sm transition-colors ${value === f ? "text-primary font-semibold" : "text-foreground hover:bg-muted"}`}>
              {f}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-card border border-border rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color }} className="mt-0.5">{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  )
}

function ChartToggle({ value, onChange, hasLine }: { value: ChartType; onChange: (v: ChartType) => void; hasLine?: boolean }) {
  return (
    <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
      <button onClick={() => onChange("bar")} className={`p-1.5 rounded-md transition-colors ${value === "bar" ? "bg-card shadow-xs" : "text-muted-foreground hover:text-foreground"}`}><BarChart2 className="w-4 h-4" /></button>
      {hasLine && <button onClick={() => onChange("line")} className={`p-1.5 rounded-md transition-colors ${value === "line" ? "bg-card shadow-xs" : "text-muted-foreground hover:text-foreground"}`}><TrendingUp className="w-4 h-4" /></button>}
      <button onClick={() => onChange("pie")} className={`p-1.5 rounded-md transition-colors ${value === "pie" ? "bg-card shadow-xs" : "text-muted-foreground hover:text-foreground"}`}><PieIcon className="w-4 h-4" /></button>
    </div>
  )
}

function MixedChart({ data, chartType, dataKeys, nameKey = "name" }: {
  data: any[]; chartType: ChartType; dataKeys: { key: string; color: string; label: string }[]; nameKey?: string
}) {
  const axis = { stroke: "currentColor", className: "text-muted-foreground", fontSize: 11 }
  if (!data?.length) return <NoData image={nodataAnalyticsImg} title="No data available" size="sm" showActionIcon={false} />
  if (chartType === "pie") {
    const flat = data.map(d => ({ name: d[nameKey], value: d[dataKeys[0].key] }))
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={flat} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="70%"
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
            {flat.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
    )
  }
  if (chartType === "line") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-border" />
          <XAxis dataKey={nameKey} {...axis} /><YAxis {...axis} />
          <Tooltip content={<CustomTooltip />} />
          {dataKeys.map(dk => <Line key={dk.key} type="monotone" dataKey={dk.key} name={dk.label} stroke={dk.color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />)}
        </LineChart>
      </ResponsiveContainer>
    )
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-border" />
        <XAxis dataKey={nameKey} {...axis} /><YAxis {...axis} />
        <Tooltip content={<CustomTooltip />} />
        {dataKeys.map(dk => <Bar key={dk.key} dataKey={dk.key} name={dk.label} fill={dk.color} radius={[4, 4, 0, 0]} />)}
      </BarChart>
    </ResponsiveContainer>
  )
}

function SectionCard({ title, subtitle, children, action, accent = "from-blue-500 to-purple-500" }: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  accent?: string;
}) {
  return (
    <div className="relative group">
      {/* Glassmorphism background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/40 dark:from-transparent dark:via-transparent dark:to-transparent dark:bg-card backdrop-blur-xl rounded-2xl border border-white/20 dark:border-border shadow-xl" />
      {/* Decorative orbs */}
      <div className="absolute top-0 right-0 w-28 h-28 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl opacity-60 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-emerald-400/20 to-blue-400/20 rounded-full blur-2xl opacity-40 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 p-5 sm:p-6 flex flex-col h-full">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2 h-2 rounded-full bg-gradient-to-r ${accent}`} />
              <h3 className="text-base font-bold text-foreground truncate">{title}</h3>
            </div>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
        <div className="flex-1 flex flex-col min-h-0">{children}</div>
      </div>
    </div>
  )
}

// ── Webinar host controls ─────────────────────────────────────────────────────
type WAlertAction =
  | { type: "end_webinar"; webinarId: string; title: string }
  | { type: "mute_all"; webinarId: string; title: string }
  | { type: "lock"; webinarId: string; title: string; locked: boolean }
  | { type: "remove_participant"; webinarId: string; participantName: string }
  | { type: "mute_participant"; webinarId: string; participantName: string; muted: boolean }
  | { type: "disable_video"; webinarId: string; participantName: string; videoOff: boolean }

function LiveWebinarPanel() {
  const t = useTranslation()
  const [wAlert, setWAlert] = useState<WAlertAction | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [wStates, setWStates] = useState<Record<string, {
    locked: boolean; mutedParticipants: Record<string, boolean>; videoOffParticipants: Record<string, boolean>
  }>>({})

  const getWS = (id: string) => wStates[id] ?? { locked: false, mutedParticipants: {}, videoOffParticipants: {} }
  const patchWS = (id: string, patch: Partial<ReturnType<typeof getWS>>) =>
    setWStates(prev => ({ ...prev, [id]: { ...getWS(id), ...patch } }))

  const confirmAction = () => {
    if (!wAlert) return
    const ws = getWS(wAlert.webinarId)
    if (wAlert.type === "end_webinar") {
      /* no local state change needed */
    } else if (wAlert.type === "mute_all") {
      const all: Record<string, boolean> = {}
      LIVE_WEBINARS.find(w => w.id === wAlert.webinarId)?.participants.forEach(p => { if (!p.isHost) all[p.name] = true })
      patchWS(wAlert.webinarId, { mutedParticipants: { ...ws.mutedParticipants, ...all } })
    } else if (wAlert.type === "lock") {
      patchWS(wAlert.webinarId, { locked: !ws.locked })
    } else if (wAlert.type === "mute_participant") {
      patchWS(wAlert.webinarId, { mutedParticipants: { ...ws.mutedParticipants, [wAlert.participantName]: !wAlert.muted } })
    } else if (wAlert.type === "disable_video") {
      patchWS(wAlert.webinarId, { videoOffParticipants: { ...ws.videoOffParticipants, [wAlert.participantName]: !wAlert.videoOff } })
    }
    setWAlert(null)
  }

  const alertMeta = wAlert ? (() => {
    if (wAlert.type === "end_webinar") return { icon: <PhoneOff className="w-5 h-5 text-red-500" />, title: t("webinar.endWebinar"), desc: `${t("webinar.endWebinarDesc", { title: wAlert.title })}`, label: t("webinar.endWebinar"), cls: "bg-red-500 hover:bg-red-600 text-white" }
    if (wAlert.type === "mute_all") return { icon: <MicOff className="w-5 h-5 text-orange-500" />, title: t("webinar.muteAll"), desc: `${t("webinar.muteAllDesc", { title: wAlert.title })}`, label: t("webinar.muteAll"), cls: "bg-orange-500 hover:bg-orange-600 text-white" }
    if (wAlert.type === "lock") return { icon: wAlert.locked ? <Unlock className="w-5 h-5 text-blue-500" /> : <Lock className="w-5 h-5 text-blue-500" />, title: wAlert.locked ? t("webinar.unlockWebinar") : t("webinar.lockWebinar"), desc: wAlert.locked ? t("webinar.unlockWebinarDesc") : t("webinar.lockWebinarDesc"), label: wAlert.locked ? t("webinar.unlock") : t("webinar.lock"), cls: "bg-blue-500 hover:bg-blue-600 text-white" }
    if (wAlert.type === "remove_participant") return { icon: <UserX className="w-5 h-5 text-red-500" />, title: t("webinar.removeAttendee"), desc: `${t("webinar.removeAttendeeDesc", { name: wAlert.participantName })}`, label: t("webinar.remove"), cls: "bg-red-500 hover:bg-red-600 text-white" }
    if (wAlert.type === "mute_participant") return { icon: wAlert.muted ? <Volume2 className="w-5 h-5 text-emerald-500" /> : <VolumeX className="w-5 h-5 text-orange-500" />, title: wAlert.muted ? `${t("webinar.unmute", { name: wAlert.participantName })}` : `${t("webinar.mute", { name: wAlert.participantName })}`, desc: wAlert.muted ? `${t("webinar.unmuteDesc", { name: wAlert.participantName })}` : `${t("webinar.muteDesc", { name: wAlert.participantName })}`, label: wAlert.muted ? t("webinar.unmuteLabel") : t("webinar.muteLabel"), cls: "bg-orange-500 hover:bg-orange-600 text-white" }
    if (wAlert.type === "disable_video") return { icon: <VideoOff className="w-5 h-5 text-slate-500 dark:text-slate-400" />, title: wAlert.videoOff ? t("webinar.enableVideo") : t("webinar.disableVideo"), desc: wAlert.videoOff ? `${t("webinar.enableVideoDesc", { name: wAlert.participantName })}` : `${t("webinar.disableVideoDesc", { name: wAlert.participantName })}`, label: wAlert.videoOff ? t("webinar.enableLabel") : t("webinar.disableLabel"), cls: "bg-slate-600 hover:bg-slate-700 text-white" }
    return null
  })() : null

  return (
    <div className="space-y-4">
      <Dialog open={!!wAlert} onOpenChange={() => setWAlert(null)}>
        <DialogContent className="max-w-sm">
          {alertMeta && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">{alertMeta.icon}</div>
                  <DialogTitle>{alertMeta.title}</DialogTitle>
                </div>
                <DialogDescription>{alertMeta.desc}</DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={() => setWAlert(null)}>{t("webinar.cancel")}</Button>
                <Button size="sm" className={alertMeta.cls} onClick={confirmAction}>{alertMeta.label}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Live Webinars Table */}
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{LIVE_WEBINARS.length} {t("webinar.webinarsCurrentlyLive")}. {t("webinar.clickRowManageParticipants")}</p>
        
        {LIVE_WEBINARS.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Video className="w-12 h-12 text-muted-foreground/20 mb-3" />
            <p className="text-sm text-muted-foreground">{t("webinar.noLiveWebinars")}</p>
          </div>
        ) : (
          <Table className="bg-card rounded-xl">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>{t("webinar.webinarTitle")}</TableHead>
                <TableHead>{t("webinar.host")}</TableHead>
                <TableHead>{t("webinar.attendees")}</TableHead>
                <TableHead>{t("webinar.started")}</TableHead>
                <TableHead className="w-24">{t("webinar.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {LIVE_WEBINARS.map((webinar) => {
                const ws = getWS(webinar.id)
                const isExpanded = expandedId === webinar.id
                return (
                  <React.Fragment key={webinar.id}>
                    <motion.tr
                      key={webinar.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      onClick={() => setExpandedId(isExpanded ? null : webinar.id)}
                      className={`cursor-pointer hover:bg-muted/50 transition-colors border-b border-border ${isExpanded ? "bg-primary/5" : ""}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="relative flex h-2 w-2 shrink-0">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                          </span>
                          <span className="font-medium text-foreground truncate max-w-[200px]">{webinar.title}</span>
                          {ws.locked && <Lock className="w-3 h-3 text-amber-500 shrink-0" />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                            style={{ background: AVATAR_COLORS[webinar.host.charCodeAt(0) % AVATAR_COLORS.length] }}>
                            {webinar.host.charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{webinar.host}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="flex -space-x-1.5">
                            {webinar.participants.slice(0, 3).map((p, idx) => (
                              <div key={idx} className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white border-2 border-card"
                                style={{ background: AVATAR_COLORS[(p.name.charCodeAt(0)) % AVATAR_COLORS.length] }} title={p.name}>
                                {p.name.charAt(0)}
                              </div>
                            ))}
                            {webinar.attendees > 3 && (
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold bg-muted text-muted-foreground border-2 border-card">
                                +{webinar.attendees - 3}
                              </div>
                            )}
                          </div>
                          <span className="text-sm font-medium">{webinar.attendees}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground whitespace-nowrap">{webinar.started}</span>
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button size="sm" className="h-7 text-xs gap-1 px-2.5">
                            <ExternalLink className="w-3 h-3" /> {t("webinar.join")}
                          </Button>
                          <button onClick={() => setExpandedId(isExpanded ? null : webinar.id)}
                            className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-muted transition-colors">
                            <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                          </button>
                        </div>
                      </TableCell>
                    </motion.tr>

                    {/* Expanded host-controls panel */}
                    <AnimatePresence>
                      {isExpanded && (
                        <tr key={`${webinar.id}-ctrl`}>
                          <td colSpan={5} className="p-0">
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="px-6 py-5 bg-muted/20 border-b border-border space-y-5">
                                {/* Webinar-level controls */}
                                <div>
                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{t("webinar.webinarControls")}</p>
                                  <div className="flex flex-wrap gap-2">
                                    <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8"
                                      onClick={() => setWAlert({ type: "mute_all", webinarId: webinar.id, title: webinar.title })}>
                                      <MicOff className="w-3.5 h-3.5 text-orange-500" /> {t("webinar.muteAll")}
                                    </Button>
                                    <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8"
                                      onClick={() => setWAlert({ type: "lock", webinarId: webinar.id, title: webinar.title, locked: ws.locked })}>
                                      {ws.locked
                                        ? <><Unlock className="w-3.5 h-3.5 text-blue-500" /> {t("webinar.unlockWebinar")}</>
                                        : <><Lock className="w-3.5 h-3.5 text-blue-500" /> {t("webinar.lockWebinar")}</>}
                                    </Button>
                                    <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8 border-red-200 text-red-600 hover:bg-red-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-900/20"
                                      onClick={() => setWAlert({ type: "end_webinar", webinarId: webinar.id, title: webinar.title })}>
                                      <PhoneOff className="w-3.5 h-3.5" /> {t("webinar.endWebinar")}
                                    </Button>
                                    <div className="flex items-center gap-1.5">
                                      {ws.locked && <Badge className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-0">{t("webinar.locked")}</Badge>}
                                    </div>
                                  </div>
                                </div>

                                {/* Per-participant controls */}
                                <div>
                                  <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("webinar.participants")}</p>
                                    <Badge variant="secondary" className="text-[10px]">{webinar.participants.length} {t("webinar.inWebinar")}</Badge>
                                  </div>
                                  <div className="space-y-1.5">
                                    {webinar.participants.map((att) => {
                                      const isMuted = !!ws.mutedParticipants[att.name]
                                      const isVideoOff = !!ws.videoOffParticipants[att.name]
                                      return (
                                        <div key={att.name} className="flex items-center gap-3 bg-card border border-border rounded-xl px-4 py-2.5 group">
                                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                                            style={{ background: AVATAR_COLORS[att.name.charCodeAt(0) % AVATAR_COLORS.length] }}>
                                            {att.name.charAt(0)}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <p className="text-sm font-medium text-foreground truncate">{att.name}</p>
                                              {att.isHost && <Badge className="text-[9px] px-1.5 py-0 bg-primary/10 text-primary border-0">{t("webinar.host")}</Badge>}
                                              {isMuted && <Badge className="text-[9px] px-1.5 py-0 bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 border-0">{t("webinar.muted")}</Badge>}
                                              {isVideoOff && <Badge className="text-[9px] px-1.5 py-0 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-0">{t("webinar.noVideo")}</Badge>}
                                            </div>
                                            <p className="text-[11px] text-muted-foreground truncate">{att.email}</p>
                                          </div>

                                          {/* Per-participant action buttons (hidden for host) - INCREASED SIZE */}
                                          {!att.isHost && (
                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <button title={isMuted ? t("webinar.unmuteLabel") : t("webinar.muteLabel")}
                                                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                                                onClick={() => setWAlert({ type: "mute_participant", webinarId: webinar.id, participantName: att.name, muted: isMuted })}>
                                                {isMuted
                                                  ? <Volume2 className="w-4 h-4 text-emerald-500" />
                                                  : <VolumeX className="w-4 h-4 text-orange-500" />}
                                              </button>
                                              <button title={isVideoOff ? t("webinar.enableVideo") : t("webinar.disableVideo")}
                                                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                                                onClick={() => setWAlert({ type: "disable_video", webinarId: webinar.id, participantName: att.name, videoOff: isVideoOff })}>
                                                <VideoOff className={`w-4 h-4 ${isVideoOff ? "text-emerald-500" : "text-slate-400"}`} />
                                              </button>
                                              <button title={t("webinar.removeFromWebinar")}
                                                className="w-8 h-8 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center transition-colors"
                                                onClick={() => setWAlert({ type: "remove_participant", webinarId: webinar.id, participantName: att.name })}>
                                                <UserX className="w-4 h-4 text-red-500" />
                                              </button>
                                            </div>
                                          )}
                                          {att.isHost && (
                                            <div className="flex items-center gap-1">
                                              <UserCheck className="w-4 h-4 text-primary" />
                                            </div>
                                          )}
                                        </div>
                                      )
                                    })}
                                  </div>
                                  <p className="text-[11px] text-muted-foreground mt-2">{t("webinar.hoverParticipantReveal")}</p>
                                </div>
                              </div>
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </AnimatePresence>
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}

// ── Participant List Section ───────────────────────────────────────────────────
function ParticipantListSection() {
  const t = useTranslation()
  const [search, setSearch] = useState("")
  const filtered = participantListData.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.email.toLowerCase().includes(search.toLowerCase())
  )

  const exportCSV = () => {
    const header = "Name,Email,Join Time,Leave Time,Duration (min),Device,Attended"
    const rows = participantListData.map(p =>
      `${p.name},${p.email},${p.joinTime},${p.leaveTime},${p.durationMin},${p.device},${p.attended ? "Yes" : "No"}`
    )
    const csv = [header, ...rows].join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a"); a.href = url; a.download = "participants.csv"; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <SectionCard
      title={t("webinar.participantList")}
      subtitle={`${participantListData.length} ${t("webinar.attendeesAcrossAllWebinars")}`}
      accent="from-teal-500 to-emerald-500"
      action={
        <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8" onClick={exportCSV}>
          <Download className="w-3.5 h-3.5" /> {t("webinar.exportCSV")}
        </Button>
      }
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-1.5 flex-1">
          <Users className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t("webinar.searchParticipants")}
            className="flex-1 bg-transparent text-xs outline-none text-foreground placeholder:text-muted-foreground"
          />
        </div>
      </div>
      <div className="overflow-x-auto overflow-y-auto max-h-[380px] rounded-xl border border-border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>{t("webinar.participant")}</TableHead>
              <TableHead>{t("webinar.join")}</TableHead>
              <TableHead>{t("webinar.leave")}</TableHead>
              <TableHead>{t("webinar.duration")}</TableHead>
              <TableHead>{t("webinar.device")}</TableHead>
              <TableHead>{t("webinar.status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p, i) => (
              <TableRow key={p.id} className="hover:bg-muted/30">
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                      style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                      {p.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{p.joinTime}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{p.leaveTime}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 bg-muted rounded-full w-12 overflow-hidden">
                      <div className="h-full bg-teal-500 rounded-full" style={{ width: `${Math.min(100, (p.durationMin / 74) * 100)}%` }} />
                    </div>
                    <span className="text-xs font-medium text-foreground">{p.durationMin} {t("webinar.min")}</span>
                  </div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.device}</TableCell>
                <TableCell>
                  <Badge className={`text-[10px] ${p.attended ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-red-500/10 text-red-600 border-red-500/20"}`}>
                    {p.attended ? t("webinar.attended") : t("webinar.leftEarly")}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </SectionCard>
  )
}

// ── Q&A Section ───────────────────────────────────────────────────────────────
function QASection() {
  const t = useTranslation()
  const [filter, setFilter] = useState<"all" | "answered" | "pending">("all")
  const filtered = qaData.filter(q => filter === "all" || q.status === filter)
  const answeredCount = qaData.filter(q => q.status === "answered").length

  return (
    <SectionCard
      title={t("webinar.qaInsights")}
      subtitle={`${qaData.length} ${t("webinar.questions")} · ${answeredCount} ${t("webinar.answeredLabel")}`}
      accent="from-purple-500 to-violet-500"
      action={
        <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
          {(["all", "answered", "pending"] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors capitalize ${filter === f ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {f}
            </button>
          ))}
        </div>
      }
    >
      <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
        {filtered.map((q, i) => (
          <motion.div
            key={q.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className={`rounded-xl border p-4 ${q.status === "answered" ? "border-emerald-500/20 bg-emerald-500/5" : "border-amber-500/20 bg-amber-500/5"}`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0 mt-0.5`}
                style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                {q.askedBy.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-medium text-foreground leading-snug">{q.question}</p>
                  <Badge className={`text-[10px] shrink-0 ${q.status === "answered" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-amber-500/10 text-amber-600 border-amber-500/20"}`}>
                    {q.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{t("webinar.askedByLabel", { name: q.askedBy })} <strong>{q.askedBy}</strong> · {q.time} · {q.upvotes} {t("webinar.upvotes")}</p>
                {q.answer && (
                  <div className="bg-card border border-border rounded-lg p-3">
                    <p className="text-xs text-foreground leading-relaxed">{q.answer}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">— {q.answeredBy}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </SectionCard>
  )
}

// ── Detail panels per card ────────────────────────────────────────────────────
function DetailPanel({ cardId, onClose }: { cardId: CardId; onClose: () => void }) {
  const t = useTranslation()
  const panels: Record<CardId, React.ReactNode> = {
    total: (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{t("webinar.allWebinarsBreakdown")}</p>
        {[
          { label: t("webinar.completed"), count: 28, color: "bg-emerald-500" },
          { label: t("webinar.upcoming"),  count: 4,  color: "bg-blue-500"    },
          { label: t("webinar.cancelled"), count: 3,  color: "bg-red-500"     },
        ].map(s => (
          <div key={s.label} className="flex items-center gap-3">
            <div className={`w-2 h-8 rounded-full ${s.color}`} />
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-foreground">{s.label}</span>
                <span className="text-foreground font-semibold">{s.count}</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={`h-full ${s.color} rounded-full`} style={{ width: `${(s.count / 35) * 100}%` }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    ),

    live: <LiveWebinarPanel />,

    registrants: (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{t("webinar.registrationBreakdownDesc")}</p>
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={registrationBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="70%"
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                {registrationBreakdown.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-2">
          {registrationBreakdown.map(d => (
            <div key={d.name} className="flex items-center gap-3 text-sm">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: d.color }} />
              <span className="flex-1 text-foreground">{d.name}</span>
              <span className="font-semibold text-foreground">{d.value.toLocaleString()}</span>
            </div>
          ))}
          <div className="pt-2 border-t border-border flex justify-between text-sm">
            <span className="text-muted-foreground">{t("webinar.attendanceRate")}</span>
            <span className="font-semibold text-emerald-500">77%</span>
          </div>
        </div>
      </div>
    ),

    attended: (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{t("webinar.attendeeJoinDropoffDesc")}</p>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={attendeeFlowData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-border" />
              <XAxis dataKey="time" fontSize={10} stroke="currentColor" className="text-muted-foreground" />
              <YAxis fontSize={10} stroke="currentColor" className="text-muted-foreground" />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="count" name="Attendees" stroke={C[0]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[t("webinar.peak"), t("webinar.avgStay"), t("webinar.dropoff")].map(([l, v]) => (
            <div key={l} className="bg-muted/40 rounded-xl py-2">
              <p className="text-xs text-muted-foreground">{l}</p>
              <p className="text-base font-bold text-foreground">{v}</p>
            </div>
          ))}
        </div>
      </div>
    ),

    cancelled: (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{t("webinar.webinarsCancelledDesc")}</p>
        {[
          { title: "Q4 Planning Session",    date: "Apr 12", reason: t("webinar.hostUnavailable") },
          { title: "Product Launch Preview", date: "Apr 14", reason: t("webinar.technicalIssues")  },
          { title: "Sales Training Recap",   date: "Apr 16", reason: t("webinar.rescheduled")       },
        ].map((w, i) => (
          <div key={i} className="flex items-start gap-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-xl p-3">
            <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">{w.title}</p>
              <p className="text-xs text-muted-foreground">{w.date} · {w.reason}</p>
            </div>
          </div>
        ))}
      </div>
    ),

  }

  const titles: Record<CardId, string> = {
    total: t("webinar.totalWebinars"), live: `${t("webinar.liveNowHostControls")}`, registrants: t("webinar.registrationBreakdown"),
    attended: t("webinar.attendeeFlow"), cancelled: t("webinar.cancelledWebinars")
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="relative group"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/40 dark:from-transparent dark:via-transparent dark:to-transparent dark:bg-card backdrop-blur-xl rounded-2xl border border-white/20 dark:border-border shadow-xl" />
      <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-primary/20 to-purple-400/20 rounded-full blur-3xl opacity-60 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tr from-emerald-400/20 to-blue-400/20 rounded-full blur-2xl opacity-40 pointer-events-none" />

      <div className="relative z-10 p-5 sm:p-6 flex flex-col h-full">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-primary to-purple-500" />
            <h3 className="text-base font-bold text-foreground">{titles[cardId]}</h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        {panels[cardId]}
      </div>
    </motion.div>
  )
}

// ── Share platforms for recordings ───────────────────────────────────────────
const SHARE_PLATFORMS = [
  { id: 'whatsapp', name: 'WhatsApp', icon: MessageSquare, color: 'bg-green-500' },
  { id: 'telegram', name: 'Telegram', icon: MessageSquare, color: 'bg-blue-500' },
  { id: 'email', name: 'Email', icon: Mail, color: 'bg-gray-500' },
  { id: 'copy', name: 'Copy Link', icon: Link2, color: 'bg-purple-500' },
]

// ── Main component ─────────────────────────────────────────────────────────────
export default function WebinarAnalyticsDashboard() {
  const t = useTranslation()
  const navigate = useNavigate()
  const [filter, setFilter] = useState("Last Week")
  const [activeCard, setActiveCard] = useState<CardId | null>(null)
  const [insightChart, setInsightChart] = useState<ChartType>("bar")
  const [engageChart, setEngageChart] = useState<ChartType>("bar")
  const [deviceChart, setDeviceChart] = useState<ChartType>("pie")
  const [showVideoPreview, setShowVideoPreview] = useState<string | null>(null)
  const [showShareDialog, setShowShareDialog] = useState<string | null>(null)
  const [shareConfig, setShareConfig] = useState({ duration: '24', unit: 'hours' })

  const STAT_CARDS: { id: CardId; title: string; value: string | number; change?: string; gradFrom: string; gradTo: string }[] = [
    { id: "total",       title: t("webinar.totalWebinars"),    value: 35,        change: "+12%", gradFrom: "from-blue-500/15",    gradTo: "to-blue-400/5"    },
    { id: "live",        title: t("webinar.liveNow"),           value: 2,         change: undefined, gradFrom: "from-emerald-500/15", gradTo: "to-emerald-400/5" },
    { id: "registrants", title: t("webinar.totalRegistrants"),  value: "1,545",   change: "+18%", gradFrom: "from-purple-500/15",  gradTo: "to-purple-400/5"  },
    { id: "attended",    title: t("webinar.attended"),           value: "1,190",   change: "+14%", gradFrom: "from-teal-500/15",    gradTo: "to-teal-400/5"    },
    { id: "cancelled",   title: t("webinar.cancelled"),          value: 3,         change: "-5%",  gradFrom: "from-red-500/15",     gradTo: "to-red-400/5"     },
  ]

  const handleCardClick = (id: CardId) => {
    if (id === "live") { navigate("/webinar/analytics/live"); return }
    if (id === "total") { navigate("/webinar/analytics/webinars"); return }
    setActiveCard(prev => prev === id ? null : id)
  }

  const handleShare = (platform: string, recordingId: string) => {
    const recording = WEBINAR_RECORDINGS.find(r => r.id === recordingId)
    if (!recording) return
    
    const shareUrl = `https://inmeet.ai/recording/${recordingId}?expires=${shareConfig.duration}${shareConfig.unit}`
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(shareUrl)
    } else if (platform === 'whatsapp') {
      window.open(`https://wa.me/?text=Check out this recording: ${recording.title} ${shareUrl}`)
    } else if (platform === 'telegram') {
      window.open(`https://t.me/share/url?url=${shareUrl}&text=Check out this recording: ${recording.title}`)
    } else if (platform === 'email') {
      window.open(`mailto:?subject=Recording: ${recording.title}&body=Check out this recording: ${shareUrl}`)
    }
    
    setShowShareDialog(null)
  }

  return (
    <div className="flex flex-col flex-1 h-full overflow-y-auto">
      {/* ── Header ── */}
      <div className="px-4 sm:px-6 py-4 border-b border-border shrink-0">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <button onClick={() => navigate("/webinar")} className="hover:text-foreground text-primary font-semibold flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-3 h-3" /> {t("webinar.webinars")}
          </button>
          <span>/</span>
          <span className="text-foreground font-medium">{t("webinar.analytics")}</span>
        </div>
        <div className="flex sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-foreground">{t("webinar.webinarAnalytics")}</h1>
            <p className="text-sm hidden sm:block text-muted-foreground mt-0.5">{t("webinar.comprehensiveOverviewDesc")} · <span className="text-primary font-medium">{t("webinar.clickLiveTotalExplore")}</span></p>
          </div>
          <div className="flex items-center gap-2">
            <FilterDropdown value={filter} onChange={setFilter} />
            <Button size="sm" className="gap-1.5 text-xs">
              <Download className="w-3.5 h-3.5" /> {t("webinar.export")}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 sm:p-6 space-y-6">
        {/* ── Stat Cards + optional detail panel ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {STAT_CARDS.map((card, i) => {
            const isPos = card.change?.startsWith("+")
            const isActive = activeCard === card.id
            return (
              <motion.button
                key={card.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => handleCardClick(card.id)}
                className={`group relative bg-card border rounded-2xl px-4 py-4 text-left overflow-hidden shadow-xs hover:shadow-md transition-all duration-200 w-full ${isActive ? "border-primary ring-2 ring-primary/20 shadow-md" : "border-border hover:border-primary/30"}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${card.gradFrom} ${card.gradTo}`}>
                  <svg
                    className="absolute bottom-0 right-0 w-full h-full"
                    viewBox="0 0 200 120"
                    fill="none"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z"
                      fill="currentColor"
                      className="text-white/30 dark:text-white/10"
                    />
                  </svg>
                </div>
                <div className="relative z-10">
                  <div className="absolute bottom-0 right-0">
                    <ArrowRight className={`w-4 h-4 transition-colors ${isActive ? "text-primary" : "text-muted-foreground/40 group-hover:text-primary"}`} />
                  </div>
                  <div className="text-xl font-bold text-foreground mb-1">{card.value}</div>
                  <div className="text-xs font-medium text-muted-foreground">{card.title}</div>
                  {card.change && (
                    <div className={`inline-flex items-center gap-1 text-[10px] font-semibold mt-2 px-1.5 py-0.5 rounded-full ${isPos ? "bg-emerald-100/60 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400" : "bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400"}`}>
                      <ArrowUpRight className={`w-2.5 h-2.5 ${!isPos ? "rotate-180" : ""}`} />{card.change}
                    </div>
                  )}
                </div>
              </motion.button>
            )
          })}
        </div>

        {/* ── Detail panel + Webinar Insights chart (side-by-side when a card is active) ── */}
        <div className={`grid gap-4 grid-cols-1 ${activeCard && activeCard !== "live" ? "lg:grid-cols-2" : "grid-cols-1"}`}>
          <AnimatePresence mode="wait">
            {activeCard && (
              <DetailPanel key={activeCard} cardId={activeCard} onClose={() => setActiveCard(null)} />
            )}
          </AnimatePresence>

          <SectionCard
            title={t("webinar.webinarInsightsRegisteredAttended")}
            subtitle={t("webinar.dailyTrendOverSelectedPeriod")}
            accent="from-blue-500 to-purple-500"
            action={<ChartToggle value={insightChart} onChange={setInsightChart} hasLine />}
          >
            <div className="h-[260px]">
              <MixedChart
                data={insightData} chartType={insightChart} nameKey="date"
                dataKeys={[
                  { key: "registered", color: C[0], label: t("webinar.registered") },
                  { key: "attended",   color: C[3], label: t("webinar.attended")   },
                ]}
              />
            </div>
            <div className="flex gap-4 mt-3">
              {[
                { label: t("webinar.withRegistration"),    val: "1,190", color: "bg-blue-500"   },
                { label: t("webinar.withoutRegistration"), val: "355",   color: "bg-orange-500" },
              ].map(b => (
                <div key={b.label} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className={`w-2.5 h-2.5 rounded-full ${b.color}`} />
                  {b.label} <strong className="text-foreground ml-1">{b.val}</strong>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* ── Engagement + Device ── */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Engagement */}
          <SectionCard
            title={t("webinar.engagementBreakdown")}
            subtitle={t("webinar.reactionsChatPolls")}
            accent="from-pink-500 to-purple-500"
            action={<ChartToggle value={engageChart} onChange={setEngageChart} />}
          >
            <div className="h-[220px]">
              <MixedChart
                data={engagementStats.map(e => ({ name: t("webinar." + e.label.toLowerCase().replace(/ /g, "")), value: e.val }))}
                chartType={engageChart} nameKey="name"
                dataKeys={[{ key: "value", color: C[2], label: t("webinar.count") }]}
              />
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {engagementStats.map(e => (
                <div key={e.label} className={`flex items-center gap-2 ${e.bg} rounded-xl px-3 py-2`}>
                  <e.icon className={`w-4 h-4 ${e.color} shrink-0`} />
                  <div>
                    <p className="text-xs text-muted-foreground leading-none">{t("webinar." + e.label.toLowerCase().replace(/ /g, ""))}</p>
                    <p className="text-sm font-semibold text-foreground">{e.val.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Device */}
          <SectionCard
            title={t("webinar.deviceUtilization")}
            subtitle={t("webinar.mobileTabletDesktop")}
            accent="from-cyan-500 to-blue-500"
            action={<ChartToggle value={deviceChart} onChange={setDeviceChart} />}
          >
            <div className="flex flex-1">
              <MixedChart
                data={deviceData} chartType={deviceChart} nameKey="name"
                dataKeys={[{ key: "value", color: C[3], label: "%" }]}
              />
            </div>
            <div className="flex items-center justify-center gap-4 mt-3">
              {deviceData.map((d, i) => (
                <div key={d.name} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: C[i] }} />
                  {d.name} <strong className="text-foreground">{d.value}%</strong>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* ── Poll Results ── */}
        <SectionCard title={t("webinar.pollResults")} subtitle={t("webinar.audienceResponsesPerQuestion")} accent="from-orange-500 to-red-500">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {pollResults.map((poll, pi) => (
              <div key={pi}>
                <p className="text-sm font-medium text-foreground mb-3">{poll.question}</p>
                <div className="space-y-6">
                  {poll.options.map((opt, oi) => {
                    const pct = Math.round((opt.votes / poll.total) * 100)
                    return (
                      <div key={oi} className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground w-24 shrink-0 truncate">{opt.text}</span>
                        <div className="flex-1 h-5 bg-muted rounded-full overflow-hidden relative">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${pct}%`, background: C[oi % C.length] }}
                          />
                          <span className="absolute inset-0 flex items-center px-2 text-[11px] font-semibold text-white">{pct}%</span>
                        </div>
                        <span className="text-xs font-semibold text-foreground w-8 text-right shrink-0">{opt.votes}</span>
                      </div>
                    )
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-2">{poll.total} {t("webinar.totalVotes")}</p>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ── Survey + Notifications 2-col ── */}
        <div className="grid gap-4 md:grid-cols-2">
        {/* ── Survey Analytics ── */}
        <SectionCard title={t("webinar.postWebinarSurvey")} subtitle={t("webinar.feedbackFromAttendees")} accent="from-amber-500 to-orange-500">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 bg-muted rounded-xl p-3 text-center">
              <p className="text-xs text-muted-foreground">{t("webinar.submissions")}</p>
              <p className="text-xl font-bold text-foreground">{surveyData.submitted.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">{t("webinar.ofLabel")} {surveyData.total.toLocaleString()}</p>
              <div className="mt-1.5 h-1.5 bg-muted-foreground/20 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: `${Math.round((surveyData.submitted / surveyData.total) * 100)}%` }} />
              </div>
              <p className="text-xs text-primary font-semibold mt-1">{Math.round((surveyData.submitted / surveyData.total) * 100)}% {t("webinar.completion")}</p>
            </div>
            <div className="flex-1 bg-muted rounded-xl p-3 text-center">
              <p className="text-xs text-muted-foreground">{t("webinar.avgRating")}</p>
              <p className="text-xl font-bold text-amber-500 flex items-center justify-center gap-1">
                <Star className="w-5 h-5 fill-amber-500" /> {surveyData.avgRating}
              </p>
              <p className="text-xs text-muted-foreground">{t("webinar.outOf5")}</p>
            </div>
          </div>
          <div className="space-y-3">
            {surveyData.questions.map((q, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-sm text-foreground flex-1">{q.q}</span>
                {q.type === "star" && (
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(q.avgScore!) ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
                    ))}
                    <span className="text-xs font-semibold text-foreground ml-1">{q.avgScore}</span>
                  </div>
                )}
                {q.type === "single" && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-emerald-500 font-semibold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {q.yes}% {t("webinar.yesLabel")}</span>
                    <span className="text-red-500 font-semibold flex items-center gap-1"><XCircle className="w-3 h-3" /> {q.no}% {t("webinar.noLabel")}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ── Notifications Analytics ── */}
        <SectionCard title={t("webinar.notificationPerformance")} subtitle={t("webinar.deliveryOpenRates")} accent="from-violet-500 to-indigo-500">
          <div className="space-y-3">
            {notificationStats.map((n, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  {i < 2 ? <Bell className="w-4 h-4 text-violet-500" /> : <Mail className="w-4 h-4 text-blue-500" />}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium text-foreground">{t("webinar." + n.label.toLowerCase().replace(/ /g, ""))}</span>
                    <span className="text-muted-foreground">{n.opened.toLocaleString()} {t("webinar.ofLabel")} {n.sent.toLocaleString()} {t("webinar.openedLabel")}</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-violet-500 rounded-full" style={{ width: `${n.rate}%` }} />
                  </div>
                </div>
                <span className="text-xs font-bold text-foreground w-8 text-right">{n.rate}%</span>
              </div>
            ))}
          </div>
        </SectionCard>
        </div>{/* end survey+notifs grid */}

        {/* ── Speakers + Top Hosts 2-col ── */}
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
        {/* ── Speakers ── */}
        <SectionCard title={t("webinar.speakerPerformance")}>
          <div className="overflow-x-auto overflow-y-auto max-h-[340px] rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t("webinar.speaker")}</TableHead>
                  <TableHead>{t("webinar.sessions")}</TableHead>
                  <TableHead>{t("webinar.avgAttendance")}</TableHead>
                  <TableHead>{t("webinar.avgRating")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {speakersData.map((s, i) => (
                  <TableRow key={s.name} className="hover:bg-muted/40">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                          style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                          {s.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{s.name}</p>
                          <p className="text-xs text-muted-foreground">{s.org}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">{s.sessions}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-muted rounded-full max-w-[60px] overflow-hidden">
                          <div className="h-full bg-primary/70 rounded-full" style={{ width: `${(s.avgAttend / 350) * 100}%` }} />
                        </div>
                        <span className="text-sm font-medium text-foreground">{s.avgAttend}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-sm font-medium text-amber-500">
                        <Star className="w-3.5 h-3.5 fill-amber-500" /> {s.rating}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>

        {/* ── Top Hosts ── */}
        <SectionCard title={t("webinar.topHosts")}>
          <div className="overflow-x-auto overflow-y-auto max-h-[340px] rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t("webinar.host")}</TableHead>
                  <TableHead>{t("webinar.webinars")}</TableHead>
                  <TableHead>{t("webinar.totalAttendees")}</TableHead>
                  <TableHead>{t("webinar.avgRating")}</TableHead>
                  <TableHead className="w-6" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {topHostsData.map((h, i) => (
                  <TableRow key={h.email} className="hover:bg-muted/40">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                          style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                          {h.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{h.name}</p>
                          <p className="text-xs text-muted-foreground">{h.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">{h.webinars}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-muted rounded-full max-w-[80px] overflow-hidden">
                          <div className="h-full bg-primary/70 rounded-full" style={{ width: `${(h.attendees / 700) * 100}%` }} />
                        </div>
                        <span className="text-sm font-medium text-foreground">{h.attendees}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-sm font-medium text-amber-500">
                        <Star className="w-3.5 h-3.5 fill-amber-500" /> {h.rating}
                      </span>
                    </TableCell>
                    <TableCell><ArrowRight className="w-3.5 h-3.5 text-muted-foreground" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
        </div>{/* end speakers+hosts grid */}


        {/* ── Participant List ── */}
        <ParticipantListSection />

        {/* ── Q&A Tab ── */}
        <QASection />

        {/* ── Live banner ── */}
        <div className="bg-gradient-to-r from-emerald-500/10 via-emerald-400/5 to-transparent border border-emerald-500/20 rounded-2xl p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
              <Radio className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                {t("webinar.webinarsLiveRightNow", { count: 2 })}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{t("webinar.clickViewLiveWebinarDetails")}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5 text-xs shrink-0 border-emerald-500/30 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
            onClick={() => handleCardClick("live")}>
            <Video className="w-3.5 h-3.5" /> {t("webinar.viewLive")}
          </Button>
        </div>

        {/* Video Preview Dialog */}
        <Dialog open={!!showVideoPreview} onOpenChange={() => setShowVideoPreview(null)}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{t("webinar.recordingPreview")}</DialogTitle>
            </DialogHeader>
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <video 
                className="w-full h-full" 
                controls 
                autoPlay
                src="https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </DialogContent>
        </Dialog>

        {/* Share Dialog */}
        <Dialog open={!!showShareDialog} onOpenChange={() => setShowShareDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t("webinar.shareRecording")}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Share duration config */}
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("webinar.accessDuration")}</label>
                <div className="flex gap-2 mt-2">
                  <Select
                    value={shareConfig.duration}
                    onValueChange={(value) => setShareConfig(prev => ({ ...prev, duration: value }))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t("webinar.duration")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="6">6</SelectItem>
                      <SelectItem value="12">12</SelectItem>
                      <SelectItem value="24">24</SelectItem>
                      <SelectItem value="48">48</SelectItem>
                      <SelectItem value="72">72</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={shareConfig.unit}
                    onValueChange={(value) => setShareConfig(prev => ({ ...prev, unit: value }))}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder={t("webinar.unit")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hours">{t("webinar.hours")}</SelectItem>
                      <SelectItem value="days">{t("webinar.days")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("webinar.linkWillExpireAfter", { duration: shareConfig.duration, unit: shareConfig.unit })}
                </p>
              </div>

              {/* Share platforms */}
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("webinar.shareVia")}</label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {SHARE_PLATFORMS.map((platform) => (
                    <button
                      key={platform.id}
                      onClick={() => handleShare(platform.id, showShareDialog!)}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted transition-colors text-left"
                    >
                      <div className={`w-8 h-8 rounded-full ${platform.color} flex items-center justify-center text-white text-sm`}>
                        <platform.icon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">{platform.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
